import {
    BeginLoadingEvent,
    EndLoadingEvent,
} from 'react-ssr-data-loader/dist/cjs/events'
import { Action } from 'redux'
import { ThunkAction } from 'redux-thunk'
import { Logger } from 'typescript-log'
import { retrieveAccessToken } from '../../authentication/helpers'
import {
    LoginEvent,
    LoginRefreshEvent,
    LogoutEvent,
    NetworkErrorEvent,
    SessionExpiredEvent,
    UserAuthEvents,
} from '../../events/user-event-types'
import { DataLayerEventName } from '../../helpers/DataLayerEventName'
import { AppState } from '../../store'
import { checkAccessTokenAndRefresh } from './auth-methods'

export type SubscriptionType =
    | 'none'
    | 'subscriber'
    | 'corporate'
    | 'corporate-site'

export interface AuthenticationState {
    isLoggedIn: boolean
    userName: string
    loginProvider: string
    socialProviders: string
    gigyaId: string
    occupantId?: string
    entitlements: string[]
    hasAttemptedValidation: boolean
    /** Bypass the paywall for a specific URL. Googlebot, MMOs, FB News etc */
    bypassSlug?: string
    sessionExpired?: boolean
    networkError?: boolean
    lastTokenValidation?: string
    subscriptionType: SubscriptionType
    subscriptionId?: string
}

interface AuthenticationPayload
    extends Omit<AuthenticationState, 'hasAttemptedValidation'> {}

export const AuthenticationActions = {
    LOGGED_IN: '@@AUTH/LOGGED_IN',
    LOGIN_REFRESHED: '@@AUTH/LOGIN_REFRESHED',
    LOGGED_OUT: '@@AUTH/LOGGED_OUT',
    ENTITLEMENTS_CHECKED: '@@AUTH/ENTITLEMENTS_CHECKED',
    PAYWALL_BYPASS: '@@AUTH/PAYWALL_BYPASS',
    SESSION_EXPIRED: '@@AUTH/SESSION_EXPIRED',
    NETWORK_ERROR: '@@AUTH/NETWORK_ERROR',
    CHECK_ACCESS_TOKEN_EXPIRY: '@@AUTH/CHECK_ACCESS_TOKEN_EXPIRY',
    AUTH_GRANT_ENTITLEMENTS: '@@AUTH/AUTH_GRANT_ENTITLEMENTS',
} as const
export type AuthenticationActions = typeof AuthenticationActions

export interface AUTH_LOGGED_IN extends Action {
    type: AuthenticationActions['LOGGED_IN']
    payload: AuthenticationPayload
}
export interface AUTH_LOGIN_REFRESHED extends Action {
    type: AuthenticationActions['LOGIN_REFRESHED']
    payload: AuthenticationPayload
}

export interface AUTH_LOGGED_OUT extends Action {
    type: AuthenticationActions['LOGGED_OUT']
}

interface PaywallBypassActionPayload {
    bypassSlug: string
    reason: string
}

interface GrantEntitlementsActionPayload {
    entitlements: string[]
}

export interface AUTH_GRANT_ENTITLEMENTS extends Action {
    type: AuthenticationActions['AUTH_GRANT_ENTITLEMENTS']
    payload: GrantEntitlementsActionPayload
}

export interface AUTH_PAYWALL_BYPASS extends Action {
    type: AuthenticationActions['PAYWALL_BYPASS']
    payload: PaywallBypassActionPayload
}
export interface AUTH_ENTITLEMENTS_CHECKED extends Action {
    type: AuthenticationActions['ENTITLEMENTS_CHECKED']
}

export interface AUTH_SESSION_EXPIRED extends Action {
    type: AuthenticationActions['SESSION_EXPIRED']
}

export interface AUTH_NETWORK_ERROR extends Action {
    type: AuthenticationActions['NETWORK_ERROR']
}

export interface AUTH_CHECK_ACCESS_TOKEN_EXPIRY extends Action {
    type: AuthenticationActions['CHECK_ACCESS_TOKEN_EXPIRY']
}

export interface AUTH_WEB_CHECK_ACCESS_TOKEN_EXPIRY_AND_REFRESH extends Action {
    type: AuthenticationActions['CHECK_ACCESS_TOKEN_EXPIRY']
}

export type AUTH_LOGIN_THUNK = ThunkAction<void, any, any, AUTH_LOGGED_IN>
export type AUTH_LOGGED_OUT_THUNK = ThunkAction<void, any, any, AUTH_LOGGED_OUT>
export type AUTH_LOGIN_REFRESHED_THUNK = ThunkAction<
    void,
    any,
    any,
    AUTH_LOGIN_REFRESHED
>
export type AUTH_SESSION_EXPIRED_THUNK = ThunkAction<
    void,
    any,
    any,
    AUTH_SESSION_EXPIRED
>
export type AUTH_NETWORK_ERROR_THUNK = ThunkAction<
    void,
    any,
    any,
    AUTH_NETWORK_ERROR
>
export type AUTH_CHECK_ACCESS_TOKEN_EXPIRY_THUNK = ThunkAction<
    void,
    AppState,
    any,
    AUTH_CHECK_ACCESS_TOKEN_EXPIRY
>

type AuthAction =
    | AUTH_ENTITLEMENTS_CHECKED
    | AUTH_GRANT_ENTITLEMENTS
    | AUTH_PAYWALL_BYPASS
    | AUTH_LOGGED_IN
    | AUTH_LOGGED_OUT
    | AUTH_LOGIN_REFRESHED
    | AUTH_SESSION_EXPIRED
    | AUTH_NETWORK_ERROR
    | AUTH_CHECK_ACCESS_TOKEN_EXPIRY

export type LoginDetails = Omit<
    AuthenticationState,
    'isLoggedIn' | 'hasAttemptedValidation' | 'isMMOReferrer'
>

export const authLogin = (
    details: LoginDetails & {
        onEvent: (loginEvent: LoginEvent) => void
    },
): AUTH_LOGIN_THUNK => {
    return (dispatch) => {
        const { onEvent, ...args } = details
        onEvent({
            originator: 'authenticator-reducer',
            type: DataLayerEventName.authLogin,
            payload: args,
        })

        return dispatch({
            type: AuthenticationActions.LOGGED_IN,
            payload: { isLoggedIn: true, ...args },
        })
    }
}

export const authRefreshLogin = (
    details: LoginDetails & {
        onEvent: (loginEvent: LoginRefreshEvent) => void
    },
): AUTH_LOGIN_REFRESHED_THUNK => {
    return (dispatch) => {
        const { onEvent, ...args } = details
        onEvent({
            originator: 'authenticator-reducer',
            type: DataLayerEventName.authLoginRefresh,
            payload: args,
        })

        return dispatch({
            type: AuthenticationActions.LOGIN_REFRESHED,
            payload: { isLoggedIn: true, ...args },
        })
    }
}

export const authLogout = (options: {
    onEvent: (logoutEvent: LogoutEvent) => void
}): AUTH_LOGGED_OUT_THUNK => {
    return (dispatch) => {
        options.onEvent({
            originator: 'authenticator-reducer',
            type: DataLayerEventName.authLogout,
            payload: {},
        })

        return dispatch({
            type: AuthenticationActions.LOGGED_OUT,
        })
    }
}

export const authEntitlementsChecked = (): AUTH_ENTITLEMENTS_CHECKED => {
    return {
        type: AuthenticationActions.ENTITLEMENTS_CHECKED,
    }
}

export const authPaywallBypass = (
    args: PaywallBypassActionPayload,
): AUTH_PAYWALL_BYPASS => {
    return {
        type: AuthenticationActions.PAYWALL_BYPASS,
        payload: {
            ...args,
        },
    }
}
export const authGrantEntitlements = (
    args: GrantEntitlementsActionPayload,
): AUTH_GRANT_ENTITLEMENTS => {
    return {
        type: AuthenticationActions.AUTH_GRANT_ENTITLEMENTS,
        payload: {
            ...args,
        },
    }
}

export const authNetworkError = (options: {
    onEvent: (networkErrorEvent: NetworkErrorEvent) => void
}): AUTH_NETWORK_ERROR_THUNK => {
    return (dispatch) => {
        options.onEvent({
            originator: 'authenticator-reducer',
            type: DataLayerEventName.authNetworkError,
            payload: {},
        })
        return dispatch({
            type: AuthenticationActions.NETWORK_ERROR,
        })
    }
}

export const authSessionExpired = (options: {
    onEvent: (sessionExpiredEvent: SessionExpiredEvent) => void
}): AUTH_SESSION_EXPIRED_THUNK => {
    return (dispatch) => {
        options.onEvent({
            originator: 'authenticator-reducer',
            type: DataLayerEventName.authSessionExpired,
            payload: {},
        })
        return dispatch({
            type: AuthenticationActions.SESSION_EXPIRED,
        })
    }
}

export interface SmediaCookieFormat {
    accessToken: string
}

export const authCheckAccessToken = (options: {
    onEvent: (
        sessionExpiredEvent:
            | UserAuthEvents
            | BeginLoadingEvent
            | EndLoadingEvent,
    ) => void
    logger: Logger
    invokation: 'manual' | 'auto'
}): AUTH_CHECK_ACCESS_TOKEN_EXPIRY_THUNK => {
    return (dispatch, getState) => {
        const { onEvent, invokation, logger } = options
        const { renditionType } = getState().render

        const accessTokenState = retrieveAccessToken()

        checkAccessTokenAndRefresh({
            renditionType,
            accessTokenState,
            dispatch,
            authState: getState().authentication,
            logger,
            onEvent,
            options: {
                invokation,
                autoRefreshToken: renditionType !== 'app',
            },
        })

        return dispatch({
            type: AuthenticationActions.CHECK_ACCESS_TOKEN_EXPIRY,
        })
    }
}

const defaultState: AuthenticationState = {
    isLoggedIn: false,
    loginProvider: '',
    socialProviders: '',
    userName: '',
    gigyaId: '',
    occupantId: '',
    entitlements: [],
    subscriptionType: 'none',
    /* Whether validation has been attempted regardless of outcome */
    hasAttemptedValidation: false,
}

export const authenticationReducer = (
    state = defaultState,
    action:
        | AuthAction
        | { type: '@@INIT' /** This represents all other actions */ },
): AuthenticationState => {
    switch (action.type) {
        case AuthenticationActions.LOGGED_IN: {
            return { ...state, ...action.payload }
        }
        case AuthenticationActions.LOGIN_REFRESHED: {
            return { ...state, ...action.payload, networkError: false }
        }
        case AuthenticationActions.LOGGED_OUT: {
            return { ...state, isLoggedIn: false, entitlements: [] }
        }
        case AuthenticationActions.AUTH_GRANT_ENTITLEMENTS: {
            return { ...state, entitlements: action.payload.entitlements }
        }
        case AuthenticationActions.ENTITLEMENTS_CHECKED: {
            return { ...state, hasAttemptedValidation: true }
        }
        case AuthenticationActions.PAYWALL_BYPASS: {
            return {
                ...state,
                ...action.payload,
            }
        }
        case AuthenticationActions.SESSION_EXPIRED: {
            return {
                ...state,
                isLoggedIn: false,
                entitlements: [],
                sessionExpired: true,
                networkError: false,
            }
        }
        case AuthenticationActions.NETWORK_ERROR: {
            return { ...state, networkError: true }
        }

        case AuthenticationActions.CHECK_ACCESS_TOKEN_EXPIRY: {
            return { ...state, lastTokenValidation: new Date().toISOString() }
        }
        default:
            return state
    }
}
